<template>
  <div v-if="showModal" class="modal-container">
    <div class="fixed inset-0 bg-black bg-opacity-40 backdrop-blur-sm z-40"></div>

    <div class="modal-content relative bg-white max-w-lg w-full max-h-[90vh] m-auto flex-col flex rounded-lg shadow-lg">
      <div class="w-full border-b-[1px] mb-4 px-6 py-4">
        <h2 class="text-[18px] text-grayDark font-bold">New Subtask</h2>
      </div>

      <form @submit.prevent="submitSubtaskModal" class="overflow-y-auto px-6">
        <div class="space-y-4 mb-4">
          <div class="">
            <label class="block text-[13px] font-medium text-gray-700">Subtask Title</label>
            <input 
              v-model="newSubtask.title" 
              type="text" 
              placeholder="Enter subtask title"
              class="mt-1 block w-full px-3 py-2 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm bg-[#F6F8F9]"
              required 
            />
          </div>
          
          <div class="">
            <label class="block text-[13px] font-medium text-gray-700">Description</label>
            <textarea 
              v-model="newSubtask.description" 
              placeholder="Enter subtask description"
              class="mt-1 block w-full h-[152px] px-3 py-2 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm bg-[#F6F8F9]"
              required
            ></textarea>
          </div>

          <div class="flex justify-between">
            <div class="">
              <label class="block text-[13px] font-medium text-gray-700">Priority</label>
              <select 
                v-model="newSubtask.priority"
                class="mt-1 block w-[220px] px-3 py-2 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm bg-[#F6F8F9]"
                required
              >
                <option value="High">High</option>
                <option value="Medium">Medium</option>
                <option value="Low">Low</option>
              </select>
            </div>
            
            <div class="">
              <label class="block text-[13px] font-medium text-gray-700">Due Date</label>
              <input 
                v-model="newSubtask.due_date" 
                type="date"
                class="mt-1 block w-[220px] px-3 py-2 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm bg-[#F6F8F9]"
                required 
              />
            </div>
          </div>

          <div class="">
            <label class="block text-[13px] font-medium text-gray-700">Assigned To</label>
            <select 
              v-if="!loading && (isAdmin || isManager)" 
              v-model="newSubtask.assigned_to"
              class="mt-1 block w-full px-3 py-2 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm bg-[#F6F8F9]"
              required
            >
              <option value="">Select team member</option>
              <option 
                v-for="member in members" 
                :key="member.talent_id" 
                :value="member.talent_id"
              >
                {{ member.first_name }} {{ member.last_name}} ({{ member.email }})
              </option>
            </select>
            
            <p 
              v-else-if="!loading"
              class="mt-1 block w-full px-3 py-2 rounded-md shadow-sm bg-[#F6F8F9] text-gray-700"
            >
              {{ talentName }} ({{ talentEmail }})
            </p>
          </div>
        </div>
        
        <div class="flex justify-end mb-4 text-[14px]">
          <button 
            type="button" 
            class="mr-4 py-2 px-4 bg-primaryLight border border-grayLight text-grayDark rounded"
            @click="closeModal"
          >
            Cancel
          </button>
          
          <button 
            type="submit"
            class="flex items-center gap-2 py-2 px-4 bg-primaryDarkBg h-[42px] text-white rounded"
            :disabled="loading"
          >
            <img src="@/assets/dashboard/add.svg" alt="User icon" class="h-4">
            {{ loading ? 'Creating...' : 'Create Subtask' }}
          </button>
        </div>
      </form>
    </div>
  </div>
</template>

<script>
import apiClient from '@/axios';

export default {
  name: 'CreateSubtaskModal',
  components: {
  },
  props: {
    showModal: {
      type: Boolean,
      required: true
    },
    parentTaskId: {
      type: [Number, String],
      required: true
    },
    projectId: {
      type: [Number, String],
      required: true
    },
    members: {
      type: Array,
      default: () => []
    },
    talentName: {
      type: String,
      default: ''
    },
    talentEmail: {
      type: String,
      default: ''
    },
    prefilledSubtask: {
      type: Object,
      default: null
    }
  },
  data() {
    return {
      loading: false,
      newSubtask: {
        assigned_to: this.prefilledSubtask ? this.prefilledSubtask.assigned_to : '',
        title: this.prefilledSubtask ? this.prefilledSubtask.title : '',
        description: this.prefilledSubtask ? this.prefilledSubtask.description : '',
        priority: this.prefilledSubtask ? this.prefilledSubtask.priority : 'Medium',
        due_date: this.prefilledSubtask ? this.prefilledSubtask.due_date : '',
        task_status: this.prefilledSubtask ? this.prefilledSubtask.task_status : 'Not Started'
      },
    };
  },
  computed: {
    isAdmin() {
      return this.$store.getters.isAdmin;
    },
    isManager() {
      return this.$store.getters.isManager;
    },
    isTalent() {
      return this.$store.getters.isTalent;
    }
  },
  methods: {
    async submitSubtaskModal() {
      // Validate required fields
      if (!this.newSubtask.title || !this.newSubtask.description || 
          !this.newSubtask.priority || !this.newSubtask.due_date || 
          !this.newSubtask.assigned_to) {
        this.$toast.error('Please fill in all required fields');
        return;
      }

      this.loading = true;

      try {
        // Format the date properly
        let formattedDate = this.newSubtask.due_date;
        if (formattedDate && !formattedDate.includes('T')) {
          formattedDate = `${formattedDate}T00:00:00.000Z`;
        }

        // Prepare subtask payload
        const subtaskPayload = {
          assigned_to: parseInt(this.newSubtask.assigned_to),
          title: this.newSubtask.title,
          description: this.newSubtask.description,
          priority: this.newSubtask.priority,
          task_status: this.newSubtask.task_status,
          due_date: formattedDate
        };

        console.log('Creating subtask with payload:', subtaskPayload);
        console.log('Parent task ID:', this.parentTaskId);

        // Create the subtask using the correct endpoint
        const response = await apiClient.post(
          `/web/tasks/${this.parentTaskId}/subtasks`, 
          subtaskPayload
        );
        
        // Show success message
        this.$toast.success('Subtask created successfully!');
        
        // Emit success event to parent
        this.$emit('subtask-created', {
          message: 'Subtask created successfully!',
          subtask: response.data.data
        });
        
        // Reset form
        this.resetForm();
        
        // Close modal after a short delay
        setTimeout(() => {
          this.$emit('close');
        }, 500);
        
      } catch (error) {
        console.error('Error creating subtask:', error);
        
        if (error.response?.status === 422) {
          const details = error.response?.data?.detail;
          if (details && Array.isArray(details)) {
            const fieldErrors = details.map(detail => {
              const field = detail.loc[detail.loc.length - 1];
              return `${field.charAt(0).toUpperCase() + field.slice(1).replace('_', ' ')}: ${detail.msg}`;
            });
            this.$toast.error(fieldErrors.join(', '));
          } else {
            this.$toast.error('Validation error. Please check your inputs.');
          }
        } else if (error.response?.status === 404) {
          this.$toast.error('Parent task not found. Please refresh and try again.');
        } else {
          this.$toast.error(error.response?.data?.message || 'An unexpected error occurred while creating the subtask.');
        }
      } finally {
        this.loading = false;
      }
    },
    
    closeModal() {
      this.$emit('close');
      this.clearMessages();
      this.resetForm();
    },
    
    clearMessages() {
      // no-op
    },
    
    resetForm() {
      this.newSubtask = {
        assigned_to: '',
        title: '',
        description: '',
        priority: 'Medium',
        due_date: '',
        task_status: 'Not Started'
      };
    },

    // Method to clear error message (called from parent)
    clearErrorMessage() {
      this.clearMessages();
    }
  },
  watch: {
    showModal(newValue) {
      console.log('Modal visibility changed:', newValue);
      console.log('Prefilled subtask:', this.prefilledSubtask);
      if (newValue) {
        this.clearMessages();
        if (this.prefilledSubtask) {
          this.newSubtask = { ...this.prefilledSubtask }; 
        } else if (this.isTalent && !this.isAdmin && !this.isManager) {
          this.newSubtask.assigned_to = this.TaskAssignedTo || '';
        }
      }
    }
  }
};
</script>

<style scoped>
.modal-container {
  display: flex;
  align-items: center;
  justify-content: center;
  position: fixed;
  inset: 0;
  z-index: 50;
}

.modal-content {
  position: relative;
  z-index: 60;
}
</style>